// Daos Ads /studiovideo — MP4 export pipeline.
//
// 1) Set up a target-resolution offscreen canvas
// 2) Drive the compositor at ~30fps in an animation loop
// 3) Capture canvas.captureStream() + a mixed audio MediaStream (original video
//    audio + optional uploaded music) → single MediaStream
// 4) MediaRecorder records it as WebM (VP9 + Opus)
// 5) After stop, transcode WebM → MP4 (H.264 + AAC) via @ffmpeg/ffmpeg (wasm)
// 6) Return { blob, url, filename }

import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import { renderFrame, loadImageEl } from "./compositor";

const LOGO_SRC = "/brand-assets/daos-logo.png";
const FFMPEG_CORE_URL = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd/ffmpeg-core.js";
const FFMPEG_WASM_URL = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd/ffmpeg-core.wasm";

let _ffmpeg = null;
export async function loadFFmpeg(onLog) {
  if (_ffmpeg?.loaded) return _ffmpeg;
  const ff = new FFmpeg();
  if (onLog) ff.on("log", ({ message }) => onLog(message));
  await ff.load({ coreURL: FFMPEG_CORE_URL, wasmURL: FFMPEG_WASM_URL });
  _ffmpeg = ff;
  return ff;
}

// Extract a WAV blob (mono 16kHz PCM) from a video File — sized for Whisper.
export async function extractAudioForWhisper(videoFile, { maxSeconds = 300 } = {}) {
  const arrayBuf = await videoFile.arrayBuffer();
  const AC = window.AudioContext || window.webkitAudioContext;
  const decodeCtx = new AC();
  let decoded;
  try {
    decoded = await decodeCtx.decodeAudioData(arrayBuf.slice(0));
  } catch (e) {
    throw new Error("No se pudo decodificar el audio del video");
  }

  const outRate = 16000;
  const channels = 1;
  const duration = Math.min(decoded.duration, maxSeconds);
  const outLen = Math.floor(duration * outRate);
  const offline = new OfflineAudioContext(channels, outLen, outRate);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start(0);
  const rendered = await offline.startRendering();
  decodeCtx.close?.();

  return audioBufferToWav(rendered);
}

function audioBufferToWav(buffer) {
  const numCh = buffer.numberOfChannels;
  const sr = buffer.sampleRate;
  const samples = buffer.length;
  const bytesPerSample = 2;
  const blockAlign = numCh * bytesPerSample;
  const byteRate = sr * blockAlign;
  const dataSize = samples * blockAlign;
  const ab = new ArrayBuffer(44 + dataSize);
  const view = new DataView(ab);
  let off = 0;
  const wStr = (s) => { for (let i = 0; i < s.length; i++) view.setUint8(off++, s.charCodeAt(i)); };
  wStr("RIFF"); view.setUint32(off, 36 + dataSize, true); off += 4;
  wStr("WAVE"); wStr("fmt ");
  view.setUint32(off, 16, true); off += 4;
  view.setUint16(off, 1, true);  off += 2;   // PCM
  view.setUint16(off, numCh, true); off += 2;
  view.setUint32(off, sr, true); off += 4;
  view.setUint32(off, byteRate, true); off += 4;
  view.setUint16(off, blockAlign, true); off += 2;
  view.setUint16(off, bytesPerSample * 8, true); off += 2;
  wStr("data"); view.setUint32(off, dataSize, true); off += 4;

  const interleaved = new Float32Array(samples * numCh);
  for (let ch = 0; ch < numCh; ch++) {
    const chData = buffer.getChannelData(ch);
    for (let i = 0; i < samples; i++) interleaved[i * numCh + ch] = chData[i];
  }
  for (let i = 0; i < interleaved.length; i++) {
    const s = Math.max(-1, Math.min(1, interleaved[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    off += 2;
  }
  return new Blob([ab], { type: "audio/wav" });
}

// Build a mixed audio stream from optional pieces.
// Returns { stream: MediaStream|null, ctx: AudioContext|null, sources: [...] }
async function buildAudioMix({ videoEl, uploadedAudio, uploadedVolume, originalMode, fadeIn, fadeOut, total }) {
  const anySource = (videoEl && originalMode !== "mute") || uploadedAudio;
  if (!anySource) return { stream: null, ctx: null, sources: [] };

  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC();
  const dest = ctx.createMediaStreamDestination();
  const sources = [];

  const applyFade = (gain, start = 0, dur = total) => {
    if (fadeIn > 0) {
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(1, ctx.currentTime + start + fadeIn);
    } else {
      gain.gain.setValueAtTime(1, ctx.currentTime + start);
    }
    if (fadeOut > 0) {
      gain.gain.setValueAtTime(1, ctx.currentTime + start + dur - fadeOut);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + start + dur);
    }
  };

  if (videoEl && originalMode !== "mute") {
    try {
      const srcNode = ctx.createMediaElementSource(videoEl);
      const gain = ctx.createGain();
      gain.gain.value = originalMode === "mix" && uploadedAudio ? 0.6 : 1;
      srcNode.connect(gain).connect(dest);
      // also route back to speakers so preview keeps playing
      gain.connect(ctx.destination);
      sources.push({ type: "video", node: srcNode });
    } catch (e) {
      // Element may already have a source; ignore.
    }
  }

  if (uploadedAudio) {
    const ab = await uploadedAudio.arrayBuffer();
    const buf = await ctx.decodeAudioData(ab);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = false;
    const gain = ctx.createGain();
    gain.gain.value = uploadedVolume;
    applyFade(gain);
    src.connect(gain).connect(dest);
    sources.push({ type: "uploaded", node: src, start: () => src.start(0) });
  }

  return { stream: dest.stream, ctx, sources };
}

// Main export flow.
// opts: { source, layers, format:{width,height}, totalSeconds, uploadedAudio?, onProgress?, onLog? }
export async function exportMp4(opts) {
  const { source, layers, format, totalSeconds } = opts;
  const onProgress = opts.onProgress || (() => {});
  const onLog = opts.onLog || (() => {});

  const W = format.width;
  const H = format.height;

  // Offscreen canvas at target resolution
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  const logo = await loadImageEl(LOGO_SRC).catch(() => null);

  const fps = 30;
  const canvasStream = canvas.captureStream(fps);

  // Prepare video element for playback (if source is a video)
  let videoEl = null;
  if (source.type === "video") {
    videoEl = source.el;
    videoEl.currentTime = 0;
    videoEl.muted = false;
    videoEl.volume = 1;
  }

  // Audio mix
  const audio = await buildAudioMix({
    videoEl,
    uploadedAudio: opts.uploadedAudio || null,
    uploadedVolume: layers.audio?.volume ?? 0.8,
    originalMode: layers.audio?.originalMode || "keep",
    fadeIn: layers.audio?.fade ? 0.4 : 0,
    fadeOut: layers.audio?.fade ? 0.6 : 0,
    total: totalSeconds,
  });

  // Combine tracks
  const finalStream = new MediaStream([
    ...canvasStream.getVideoTracks(),
    ...(audio.stream ? audio.stream.getAudioTracks() : []),
  ]);

  const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
    ? "video/webm;codecs=vp9,opus"
    : "video/webm";
  const recorder = new MediaRecorder(finalStream, { mimeType: mime, videoBitsPerSecond: 6_000_000 });

  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

  const stopPromise = new Promise((resolve) => { recorder.onstop = () => resolve(); });

  // Kick off animation loop
  let raf = 0;
  const startTs = performance.now();
  const drawLoop = () => {
    const t = (performance.now() - startTs) / 1000;
    const tt = Math.min(totalSeconds, t);
    renderFrame(ctx, source, layers, { W, H, total: totalSeconds, t: tt, logo });
    onProgress(Math.min(1, t / totalSeconds));
    if (t < totalSeconds) {
      raf = requestAnimationFrame(drawLoop);
    } else {
      recorder.stop();
      if (videoEl) videoEl.pause();
    }
  };

  // Start playback + recording synchronously
  recorder.start(200);
  if (videoEl) {
    try { await videoEl.play(); } catch (e) { /* autoplay may fail silently */ }
  }
  // Kick off buffer source(s)
  audio.sources.forEach((s) => { if (s.start) s.start(); });
  raf = requestAnimationFrame(drawLoop);

  await stopPromise;
  cancelAnimationFrame(raf);
  try { audio.ctx?.close(); } catch (_) { /* noop */ }
  const webmBlob = new Blob(chunks, { type: mime });

  onLog(`WebM listo (${(webmBlob.size / 1024 / 1024).toFixed(1)}MB) · transcodificando a MP4…`);

  // Transcode to MP4
  const ff = await loadFFmpeg((msg) => onLog(msg));
  const inputName = "in.webm";
  const outputName = "out.mp4";
  await ff.writeFile(inputName, await fetchFile(webmBlob));
  // H.264 + AAC, faststart for streaming
  await ff.exec([
    "-i", inputName,
    "-c:v", "libx264",
    "-preset", "veryfast",
    "-crf", "20",
    "-pix_fmt", "yuv420p",
    "-c:a", "aac",
    "-b:a", "128k",
    "-movflags", "+faststart",
    outputName,
  ]);
  const mp4Data = await ff.readFile(outputName);
  const mp4Blob = new Blob([mp4Data.buffer], { type: "video/mp4" });
  try { ff.deleteFile(inputName); ff.deleteFile(outputName); } catch (_) { /* noop */ }

  const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
  return {
    blob: mp4Blob,
    url: URL.createObjectURL(mp4Blob),
    filename: `daos-${format.width}x${format.height}-${stamp}.mp4`,
  };
}
